import React, { useEffect, useRef, useState } from "react";

const sizeClasses = {
  xl: "text-3xl sm:text-4xl",
  lg: "text-2xl sm:text-3xl",
  md: "text-xl sm:text-2xl",
};

let gravityStyleInjected = false;
function ensureGravityStyles() {
  if (gravityStyleInjected || typeof document === "undefined") return;
  gravityStyleInjected = true;
  const style = document.createElement("style");
  style.setAttribute("data-live-counter-gravity", "true");
  style.textContent = `
    @keyframes live-counter-gravity-fall {
      0% {
        opacity: 1;
        transform: translateY(-8px) scale(1.05);
      }
      55% {
        opacity: 1;
        transform: translateY(10px) scale(1);
      }
      75% {
        transform: translateY(4px) scale(1.02);
      }
      100% {
        opacity: 0;
        transform: translateY(28px) scale(0.92);
      }
    }
    @keyframes live-counter-gravity-impact {
      0% { transform: translateY(0); }
      30% { transform: translateY(4px); }
      55% { transform: translateY(-2px); }
      100% { transform: translateY(0); }
    }
    .animate-live-counter-gravity {
      animation: live-counter-gravity-fall 1.15s cubic-bezier(0.22, 0.9, 0.35, 1) forwards;
    }
    .animate-live-counter-impact {
      animation: live-counter-gravity-impact 0.45s ease-out;
    }
  `;
  document.head.appendChild(style);
}

export function AnimatedLiveCounter({
  value = 0,
  className = "",
  size = "lg",
  showDelta = true,
  gravity = false,
  format = (n) => n.toLocaleString("en-IN"),
}) {
  const safeValue = Number(value) || 0;
  const [display, setDisplay] = useState(safeValue);
  const [delta, setDelta] = useState(null);
  const [pulsing, setPulsing] = useState(false);
  const [impact, setImpact] = useState(false);
  const prevTarget = useRef(safeValue);
  const rafRef = useRef(null);

  useEffect(() => {
    if (gravity) ensureGravityStyles();
  }, [gravity]);

  useEffect(() => {
    const from = prevTarget.current;
    const to = safeValue;

    if (from === to) {
      setDisplay(to);
      return undefined;
    }

    if (showDelta && to > from) {
      const bump = to - from;
      setDelta(bump);
      setPulsing(true);
      if (gravity) {
        setImpact(true);
      }
      const deltaTimer = setTimeout(() => setDelta(null), gravity ? 1200 : 1400);
      const pulseTimer = setTimeout(() => setPulsing(false), 800);
      const impactTimer = gravity
        ? setTimeout(() => setImpact(false), 500)
        : null;

      const diff = bump;
      const duration = Math.min(1400, 350 + diff * 12);
      const start = performance.now();

      const step = (now) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - (1 - t) ** 3;
        const current = Math.round(from + (to - from) * eased);
        setDisplay(current);
        if (t < 1) {
          rafRef.current = requestAnimationFrame(step);
        } else {
          prevTarget.current = to;
          setDisplay(to);
        }
      };

      rafRef.current = requestAnimationFrame(step);

      return () => {
        clearTimeout(deltaTimer);
        clearTimeout(pulseTimer);
        if (impactTimer) clearTimeout(impactTimer);
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
      };
    }

    prevTarget.current = to;
    setDisplay(to);
    return undefined;
  }, [safeValue, showDelta, gravity]);

  return (
    <span
      className={`relative inline-block tabular-nums leading-none transition-transform duration-300 ${
        sizeClasses[size] || sizeClasses.lg
      } ${pulsing && !gravity ? "scale-105" : "scale-100"} ${
        impact ? "animate-live-counter-impact" : ""
      } ${className}`}
    >
      <span
        className={`inline-block font-bold transition-colors duration-300 ${
          pulsing ? "text-red-500" : "text-inherit"
        }`}
      >
        {format(display)}
      </span>
      {delta != null && delta > 0 && (
        <span
          className={`absolute pointer-events-none whitespace-nowrap font-black ${
            gravity
              ? "left-1/2 -translate-x-1/2 top-0 text-sm sm:text-base text-emerald-500 animate-live-counter-gravity"
              : "absolute -top-1 left-full ml-1 text-xs font-bold text-green-600 dark:text-green-400 animate-live-delta"
          }`}
          aria-hidden
        >
          +{format(delta)}
        </span>
      )}
    </span>
  );
}

export function LiveEntriesDisplay({
  entries = 0,
  total = null,
  live = false,
  accentColor = "#D32F2F",
  className = "",
  compact = false,
}) {
  const safeEntries = Number(entries) || 0;
  const safeTotal = total != null ? Number(total) : null;
  const pct =
    safeTotal && safeTotal > 0
      ? Math.min(100, (safeEntries / safeTotal) * 100)
      : 0;
  const [recentTick, setRecentTick] = useState(false);
  const prevEntries = useRef(safeEntries);

  useEffect(() => {
    if (safeEntries > prevEntries.current) {
      setRecentTick(true);
      const t = setTimeout(() => setRecentTick(false), 2000);
      prevEntries.current = safeEntries;
      return () => clearTimeout(t);
    }
    prevEntries.current = safeEntries;
    return undefined;
  }, [safeEntries]);

  const showLive = live || recentTick;

  return (
    <div className={`relative ${className}`}>
      <div className="flex items-center gap-2 mb-1">
        <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
          Entries
        </p>
        {showLive && (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide text-white"
            style={{ backgroundColor: accentColor }}
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
            </span>
            Live
          </span>
        )}
      </div>
      <p className="leading-none break-all">
        <AnimatedLiveCounter
          value={safeEntries}
          size={compact ? "md" : "xl"}
          className={compact ? "!text-xl" : undefined}
        />
        {safeTotal != null && safeTotal > 0 && (
          <span
            className={`font-semibold text-gray-500 dark:text-gray-400 ${
              compact ? "text-sm" : "text-xl sm:text-2xl"
            }`}
          >
            {" "}
            / {safeTotal.toLocaleString("en-IN")}
          </span>
        )}
      </p>
      {safeTotal != null && safeTotal > 0 && (
        <div
          className={`bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden ${
            compact ? "mt-2 h-1.5" : "mt-2.5 h-2"
          }`}
        >
          <div
            className="h-full rounded-full transition-all duration-700 ease-out"
            style={{
              width: `${Math.max(pct, safeEntries > 0 ? 0.4 : 0)}%`,
              backgroundColor: accentColor,
              boxShadow: recentTick ? `0 0 8px ${accentColor}` : "none",
            }}
          />
        </div>
      )}
      <p className="text-[10px] text-gray-400 mt-1">
        Wishes used to join this round
      </p>
    </div>
  );
}

export function LiveLeaderboardCount({
  count = 0,
  live = false,
  accentColor = "#D32F2F",
  icon: Icon = null,
  className = "",
}) {
  const safeCount = Number(count) || 0;
  const [recentTick, setRecentTick] = useState(false);
  const prev = useRef(safeCount);

  useEffect(() => {
    if (safeCount > prev.current) {
      setRecentTick(true);
      const t = setTimeout(() => setRecentTick(false), 2000);
      prev.current = safeCount;
      return () => clearTimeout(t);
    }
    prev.current = safeCount;
    return undefined;
  }, [safeCount]);

  return (
    <div className={className}>
      <div className="flex items-center gap-1.5 mb-1">
        <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
          Leaderboard
        </p>
        {(live || recentTick) && (
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
        )}
      </div>
      <p className="flex items-center gap-1.5 leading-none">
        {Icon && (
          <Icon className="w-5 h-5 shrink-0" style={{ color: accentColor }} />
        )}
        <AnimatedLiveCounter value={safeCount} size="lg" />
      </p>
      <p className="text-[10px] text-gray-400 mt-1 leading-snug">
        Players who started the quiz (on the scoreboard)
      </p>
    </div>
  );
}

export default AnimatedLiveCounter;
